import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* A row from /roomParentAdmin/donationMethods/:networkId — one way to pay the
   class fund. `method` is the API enum (zelle | venmo | applePay | other);
   `methodName` is the display name "other" carries and is "" for the named
   rails. `handleType` (phone | email | username) is the server's reading of
   the handle, used to format it for parents. */
function normalizeDonationMethod(m) {
  return {
    id: m?._id || m?.id || '',
    method: m?.method || 'other',
    methodName: m?.methodName || '',
    /* the phone, email or username a parent types into their own app */
    handle: m?.handle || '',
    handleType: m?.handleType || '',
    /* whose account the handle is — "" when the room parent left it blank */
    recipientName: m?.recipientName || '',
    /* Venmo only: last 4 digits of the account's phone — "" for the others,
       and for Venmo methods saved before the field existed */
    last4: m?.last4 || '',
    status: m?.status || 'active',
    /* the room parent who added it — the API returns the id only */
    createdBy: m?.createdBy || '',
  };
}

const donationMethodsService = {
  /* Every method on the network, oldest first — the order they were added.
     Defaults to active only, because a deactivated method is hidden from
     parents and is what `deactivate` below leaves behind. */
  list: async (networkId, { page = 1, limit = 50, status = 'active', method } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION_METHODS_BY_NETWORK(networkId), {
      params: {
        page,
        limit,
        ...(status ? { status } : {}),
        ...(method ? { method } : {}),
      },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.donationMethods || data?.docs || data?.items || data?.results || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? data?.count ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    return { methods: rawList.map(normalizeDonationMethod), total, totalPages };
  },

  /* { networkId, method, handle, methodName (optional, 'other' only),
     handleType (optional — the API detects it from the handle otherwise),
     recipientName (optional, up to 100 chars),
     last4 (Venmo only: exactly 4 digits, or "") }.
     Rejects with 409 when this network already has that method + handle. */
  create: (fields) => apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION_METHOD, fields),

  /* { id, ...only what changed }; same 409 on a clashing method + handle.
     recipientName: '' clears it. */
  update: (patch) => apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION_METHOD, patch),

  /* There is no delete endpoint. Deactivating takes the method off the public
     page and out of the default listing, which is what Remove means here. */
  deactivate: (id) => apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.DONATION_METHOD, { id, status: 'inactive' }),
};

export default donationMethodsService;
